import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ChevronRight } from 'lucide-react';
import { useTheme } from './Navbar';

const WORDS = ['PATRIMONIO', 'FUTURO', 'CALIDAD'];

const Hero = () => {
  const [scrollY, setScrollY] = useState(0);
  const [typed, setTyped] = useState('');
  const heroRef = useRef(null);
  const [wordIdx, setWordIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIdx, setCharIdx] = useState(0);
  const { isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const word = WORDS[wordIdx];
    const speed = isDeleting ? 60 : 120;
    const timer = setTimeout(() => {
      if (!isDeleting && charIdx < word.length) {
        setTyped(word.slice(0, charIdx + 1));
        setCharIdx(charIdx + 1);
      } else if (isDeleting && charIdx > 0) {
        setTyped(word.slice(0, charIdx - 1));
        setCharIdx(charIdx - 1);
      } else if (!isDeleting && charIdx === word.length) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && charIdx === 0) {
        setIsDeleting(false);
        setWordIdx((prev) => (prev + 1) % WORDS.length);
      }
    }, speed);
    return () => clearTimeout(timer);
  }, [charIdx, isDeleting, wordIdx]);

  const scrollToNext = () => document.getElementById('nosotros')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section 
      id="inicio" 
      ref={heroRef} 
      style={{ 
        position: 'relative', 
        height: '100vh', 
        minHeight: 600, 
        overflow: 'hidden', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        background: isDark ? '#0d0d0d' : '#ffffff' 
      }}
    >

      {/* BG Image with Parallax */}
      <div style={{
        position: 'absolute', inset: '-10%',
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/4cq91uu3_9.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transform: `translateY(${scrollY * 0.3}px)`,
        transition: 'transform 0.1s linear',
      }} />

      {/* Overlay - changes based on theme */}
      <div style={{ 
        position: 'absolute', 
        inset: 0, 
        background: isDark 
          ? 'linear-gradient(135deg, rgba(13,13,13,0.95) 0%, rgba(13,13,13,0.85) 55%, rgba(196,30,58,0.2) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.75) 55%, rgba(196,30,58,0.12) 100%)'
      }} />

      {/* Left accent line */}
      <div style={{ position: 'absolute', left: 0, top: '20%', bottom: '20%', width: 4, background: 'var(--red)', borderRadius: '0 2px 2px 0' }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2, maxWidth: 1200, width: '100%', padding: '0 1.5rem', margin: '0 auto' }}>
        <div style={{ maxWidth: 800 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', animation: 'fadeInDown 0.8s ease 0.2s both' }}>
            <div style={{ width: 32, height: 2, background: 'var(--red)', flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--red)' }}>Más de 12 años · Oaxaca</span>
          </div>

          <h1 style={{ 
            fontFamily: 'var(--font-heading)', 
            fontWeight: 900, 
            fontSize: 'clamp(2.5rem, 8vw, 6.5rem)', 
            color: isDark ? '#ffffff' : '#111111', 
            lineHeight: 1.0, 
            letterSpacing: '-0.02em', 
            marginBottom: '1.5rem', 
            animation: 'fadeInUp 0.8s ease 0.4s both' 
          }}>
            CONSTRUIMOS
            <br />
            TU{' '}
            <span style={{ color: 'var(--red)', position: 'relative', display: 'inline-block' }}>
              {typed}<span className="cursor" />
            </span>
          </h1>

          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(0.9rem, 2vw, 1.15rem)',
            color: isDark ? 'rgba(255,255,255,0.7)' : '#374151',
            lineHeight: 1.75,
            maxWidth: 560,
            marginBottom: '0.75rem',
            animation: 'fadeInUp 0.8s ease 0.6s both'
          }}>
            Construimos espacios que generan valor.{' '}
            <strong style={{ color: isDark ? '#ffffff' : '#111111' }}>
              Más de 12 años
            </strong>{' '}
            desarrollando proyectos residenciales, comerciales y obra pública en Oaxaca.
          </p>

          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(0.8rem, 1.5vw, 0.95rem)',
            color: isDark ? 'rgba(255,255,255,0.45)' : '#6b7280',
            lineHeight: 1.6,
            maxWidth: 440,
            marginBottom: '2.5rem',
            animation: 'fadeInUp 0.8s ease 0.7s both',
            fontStyle: 'italic',
          }}>
            Desde tu casa ideal hasta proyectos de gran escala.
          </p>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', animation: 'fadeInUp 0.8s ease 0.8s both' }}>
            <button onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })} className="btn-red">
              Cotiza tu proyecto hoy <ChevronRight size={16} />
            </button>
            <button 
              onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })} 
              className="btn-outline"
              style={{
                color: isDark ? '#ffffff' : '#111111',
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : '#e5e7eb'
              }}
            >
              Contáctanos
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button 
        onClick={scrollToNext} 
        style={{ 
          position: 'absolute', 
          bottom: '2.5rem', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          background: 'none', 
          border: isDark ? '2px solid rgba(255,255,255,0.2)' : '2px solid #e5e7eb', 
          borderRadius: '50%', 
          width: 48, 
          height: 48, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          cursor: 'pointer', 
          color: isDark ? '#ffffff' : '#111111', 
          zIndex: 2, 
          animation: 'float 2.5s ease-in-out infinite', 
          transition: 'border-color 0.3s ease' 
        }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--red)'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.2)' : '#e5e7eb'}
      >
        <ArrowDown size={20} />
      </button>

      <style>{`
        @keyframes fadeInDown { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
};

export default Hero;
